import * as THREE from 'three';
import { findBodies, type BodyDef } from '../data/bodies';
import { formatDistance, formatSpeed } from '../scale/units';
import type { CelestialBody } from '../scene/CelestialBody';

export class HUD {
  private searchInput: HTMLInputElement;
  private resultsEl: HTMLUListElement;
  private distEl: HTMLElement;
  private speedEl: HTMLElement;
  private nearEl: HTMLElement;
  private focusEl: HTMLElement;
  private focusName: HTMLElement;
  private focusMeta: HTMLElement;
  private hintEl: HTMLElement;
  private labelLayer: HTMLElement;
  private labels = new Map<string, HTMLDivElement>();
  private activeIndex = 0;
  private results: BodyDef[] = [];
  private onTeleport: (id: string) => void;
  private camera: THREE.PerspectiveCamera;
  private raycaster = new THREE.Raycaster();
  private hoverBody: CelestialBody | null = null;

  constructor(
    camera: THREE.PerspectiveCamera,
    onTeleport: (id: string) => void,
  ) {
    this.camera = camera;
    this.onTeleport = onTeleport;

    this.searchInput = document.getElementById('search-input') as HTMLInputElement;
    this.resultsEl = document.getElementById('search-results') as HTMLUListElement;
    this.distEl = document.getElementById('dist-value')!;
    this.speedEl = document.getElementById('speed-value')!;
    this.nearEl = document.getElementById('near-value')!;
    this.focusEl = document.getElementById('body-focus')!;
    this.focusName = document.getElementById('focus-name')!;
    this.focusMeta = document.getElementById('focus-meta')!;
    this.hintEl = document.getElementById('hint')!;

    this.labelLayer = document.createElement('div');
    this.labelLayer.id = 'label-layer';
    this.labelLayer.style.cssText =
      'position:absolute;inset:0;pointer-events:none;z-index:9;overflow:hidden;';
    document.getElementById('hud')!.appendChild(this.labelLayer);

    this.bindSearch();

    // Fade hint after first interaction
    const fade = () => {
      this.hintEl.classList.add('fade');
      window.removeEventListener('keydown', fade);
      window.removeEventListener('mousedown', fade);
    };
    window.addEventListener('keydown', fade);
    window.addEventListener('mousedown', fade);
    setTimeout(fade, 14000);
  }

  private bindSearch(): void {
    this.searchInput.addEventListener('input', () => {
      this.refreshResults(this.searchInput.value);
    });

    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
        this.renderResults();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.renderResults();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (this.results[this.activeIndex]) {
          this.select(this.results[this.activeIndex]);
        }
      } else if (e.key === 'Escape') {
        this.searchInput.blur();
        this.closeResults();
        this.searchInput.value = '';
      }
    });

    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value) this.refreshResults(this.searchInput.value);
    });

    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('#search-wrap')) {
        this.closeResults();
      }
    });
  }

  private refreshResults(q: string): void {
    this.results = findBodies(q).slice(0, 10);
    this.activeIndex = 0;
    this.renderResults();
  }

  private renderResults(): void {
    if (!this.results.length) {
      this.closeResults();
      return;
    }
    this.resultsEl.classList.add('open');
    this.resultsEl.innerHTML = '';
    this.results.forEach((b, i) => {
      const li = document.createElement('li');
      if (i === this.activeIndex) li.classList.add('active');
      li.innerHTML = `<span>${b.name}</span><span class="kind">${b.kind}</span>`;
      li.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.select(b);
      });
      this.resultsEl.appendChild(li);
    });
  }

  private closeResults(): void {
    this.resultsEl.classList.remove('open');
    this.resultsEl.innerHTML = '';
  }

  private select(b: BodyDef): void {
    this.searchInput.value = b.name;
    this.closeResults();
    this.searchInput.blur();
    // Release pointer lock so teleport feels intentional
    if (document.pointerLockElement) document.exitPointerLock();
    this.onTeleport(b.id);
  }

  ensureLabels(bodies: CelestialBody[]): void {
    for (const b of bodies) {
      if (this.labels.has(b.def.id)) continue;
      const el = document.createElement('div');
      el.className = 'world-label';
      el.textContent = b.def.name;
      this.labelLayer.appendChild(el);
      this.labels.set(b.def.id, el);
    }
  }

  update(
    bodies: CelestialBody[],
    camera: THREE.PerspectiveCamera,
    speed: number,
    nearest: CelestialBody | null,
    nearestDist: number,
  ): void {
    // Readouts
    this.speedEl.textContent = formatSpeed(speed);
    if (nearest) {
      this.nearEl.textContent = nearest.def.name;
      this.distEl.textContent = formatDistance(nearestDist);
      this.focusName.textContent = nearest.def.name;
      this.focusMeta.textContent =
        [nearest.def.description, nearest.def.kind].filter(Boolean).join(' · ') ||
        nearest.def.kind;
      this.focusEl.classList.add('visible');
    } else {
      this.nearEl.textContent = '—';
      this.distEl.textContent = '—';
      this.focusEl.classList.remove('visible');
    }

    // Hover labels — project nearby bodies
    const w = window.innerWidth;
    const h = window.innerHeight;
    const camPos = camera.position;
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);

    for (const body of bodies) {
      const el = this.labels.get(body.def.id);
      if (!el) continue;
      if (body.def.kind === 'star') {
        el.classList.remove('visible');
        continue;
      }

      const pos = body.worldPosition;
      const toBody = pos.clone().sub(camPos);
      const dist = toBody.length();
      const ndot = toBody.normalize().dot(fwd);

      // Show labels for bodies in view and within a dynamic distance
      const sizeFactor = Math.max(body.radius, 0.05);
      const showDist = Math.max(sizeFactor * 80, 40) * (body.def.kind === 'planet' || body.def.kind === 'dwarf' ? 3 : 1.2);

      if (ndot < 0.25 || dist > showDist || dist < body.collisionRadius * 1.2) {
        el.classList.remove('visible');
        continue;
      }

      const projected = pos.clone().project(camera);
      if (projected.z > 1) {
        el.classList.remove('visible');
        continue;
      }
      const x = (projected.x * 0.5 + 0.5) * w;
      const y = (-projected.y * 0.5 + 0.5) * h;
      if (x < 0 || x > w || y < 0 || y > h) {
        el.classList.remove('visible');
        continue;
      }
      el.style.left = `${x}px`;
      el.style.top = `${y - 8}px`;
      el.classList.add('visible');
    }

    // Crosshair hover ray
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    let best: CelestialBody | null = null;
    let bestT = Infinity;
    for (const body of bodies) {
      const to = body.worldPosition.clone().sub(this.raycaster.ray.origin);
      const proj = to.dot(this.raycaster.ray.direction);
      if (proj < 0) continue;
      const closest = this.raycaster.ray.origin
        .clone()
        .addScaledVector(this.raycaster.ray.direction, proj);
      const d = closest.distanceTo(body.worldPosition);
      if (d < body.radius * 1.4 && proj < bestT) {
        bestT = proj;
        best = body;
      }
    }
    this.hoverBody = best;
  }

  getHoverBody(): CelestialBody | null {
    return this.hoverBody;
  }
}
